import { Project } from '../../types';

export type HistoryAction = {
  type: 'ADD_STATION' | 'DELETE_STATION' | 'EDIT_STATION' | 'ADD_ROUTE' | 'DELETE_ROUTE' | 'MOVE_STATION' | 'EDIT_DISTANCE' | 'UPDATE_ROUTE';
  data: any;
  timestamp: number;
  projectId: string;
};

export class UndoManager {
  private static readonly MAX_HISTORY = 50;
  private static readonly STORAGE_KEY = 'railmapper_history';
  private static readonly HISTORY_VERSION = 1;  // Increment this when making breaking changes

  private undoStack: HistoryAction[] = [];
  private redoStack: HistoryAction[] = [];
  private projectId: string;

  constructor(projectId: string) {
    this.projectId = projectId;
    this.loadHistory();
  }

  private validateAction(action: any): action is HistoryAction {
    // Basic structure validation
    if (!action ||
        typeof action !== 'object' ||
        typeof action.type !== 'string' ||
        typeof action.data !== 'object' ||
        typeof action.timestamp !== 'number' ||
        typeof action.projectId !== 'string' ||
        action.projectId !== this.projectId) {
      return false;
    }

    // Validate action type
    const validTypes = [
      'ADD_STATION',
      'DELETE_STATION',
      'EDIT_STATION',
      'ADD_ROUTE',
      'DELETE_ROUTE',
      'MOVE_STATION',
      'EDIT_DISTANCE',
      'UPDATE_ROUTE'
    ];
    if (!validTypes.includes(action.type)) {
      return false;
    }

    // Validate data structure based on action type
    switch (action.type) {
      case 'MOVE_STATION':
        return (
          typeof action.data.stationId === 'string' &&
          typeof action.data.previousPosition?.x === 'number' &&
          typeof action.data.previousPosition?.y === 'number' &&
          typeof action.data.newPosition?.x === 'number' &&
          typeof action.data.newPosition?.y === 'number'
        );
      case 'ADD_STATION':
      case 'DELETE_STATION':
        return (
          action.data.station &&
          typeof action.data.station.id === 'string' &&
          typeof action.data.station.number === 'number'
        );
      case 'ADD_ROUTE':
      case 'DELETE_ROUTE':
        return (
          action.data.route &&
          typeof action.data.route.id === 'string' &&
          typeof action.data.route.source === 'string' &&
          typeof action.data.route.target === 'string'
        );
      case 'UPDATE_ROUTE':
        return (
          typeof action.data.routeId === 'string' &&
          action.data.previousRoute &&
          action.data.newRoute
        );
      default:
        return true;
    }
  }

  private validateHistoryStack(stack: any[]): HistoryAction[] {
    if (!Array.isArray(stack)) return [];
    return stack.filter(action => this.validateAction(action));
  }

  private loadHistory() {
    try {
      const storedHistory = localStorage.getItem(`${UndoManager.STORAGE_KEY}_${this.projectId}`);
      if (storedHistory) {
        const parsed = JSON.parse(storedHistory);
        
        // Check version and structure
        if (parsed && 
            typeof parsed === 'object' && 
            parsed.version === UndoManager.HISTORY_VERSION &&
            Array.isArray(parsed.undoStack) &&
            Array.isArray(parsed.redoStack)) {
          
          // Validate and filter out invalid actions
          const validatedUndoStack = this.validateHistoryStack(parsed.undoStack);
          const validatedRedoStack = this.validateHistoryStack(parsed.redoStack);
          
          // Only set the stacks if they contain valid actions
          if (validatedUndoStack.length === 0 && validatedRedoStack.length === 0) {
            console.log('No valid history found, clearing history');
            this.clear();
          } else {
            this.undoStack = validatedUndoStack;
            this.redoStack = validatedRedoStack;
            console.log('Loaded history:', {
              undoStackSize: this.undoStack.length,
              redoStackSize: this.redoStack.length
            });
          }
        } else {
          console.log('Clearing history due to version mismatch or invalid format');
          this.clear();
        }
      }
    } catch (error) {
      console.error('Failed to load history:', error);
      this.undoStack = [];
      this.redoStack = [];
    }
  }

  private saveHistory() {
    try {
      const data = {
        version: UndoManager.HISTORY_VERSION,
        undoStack: this.undoStack,
        redoStack: this.redoStack
      };
      localStorage.setItem(
        `${UndoManager.STORAGE_KEY}_${this.projectId}`,
        JSON.stringify(data)
      );
    } catch (error) {
      console.error('Failed to save history:', error);
    }
  }

  public addAction(action: Omit<HistoryAction, 'timestamp' | 'projectId'>) {
    const fullAction: HistoryAction = {
      ...action,
      timestamp: Date.now(),
      projectId: this.projectId
    };

    this.undoStack.push(fullAction);
    if (this.undoStack.length > UndoManager.MAX_HISTORY) {
      this.undoStack.shift();
    }
    this.redoStack = []; // Clear redo stack when new action is added
    this.saveHistory();
  }

  public canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  public canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  public undo(): HistoryAction | null {
    if (!this.canUndo()) return null;

    const action = this.undoStack.pop()!;
    this.redoStack.push(action);
    this.saveHistory();
    return action;
  }

  public redo(): HistoryAction | null {
    if (!this.canRedo()) return null;

    const action = this.redoStack.pop()!;
    this.undoStack.push(action);
    this.saveHistory();
    return action;
  }

  public clear() {
    this.undoStack = [];
    this.redoStack = [];
    this.saveHistory();
  }
} 